import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react'
import { useSelector } from 'react-redux'
import {
  chatCredentialsSelectors,
  usersSelectors
} from 'store'
import {
  Avatar,
  Box,
  Flex,
  Text
} from '@chakra-ui/react'
import { ImagesPng } from 'assets/images'
import { getProfileIdFromMatrixId } from 'utils/helpers'
import { COLORS } from 'utils/styles'

import { InputBar } from './InputBar'
import { MsgFunctionBar } from './MsgFunctionBar'
import { QuoteMessage } from './QuoteMessage'
import { getMatrixClient } from '../../Matrix/clientStore'

export const ChatRoom = ({
  goToMarker,
  roomId
}) => {
  const [messages, setMessages] = useState([])
  const [members, setMembers] = useState([])
  const [otherUserId, setOtherUserId] = useState(null)
  const [memberCheck, setMemberCheck] = useState(false)
  const [hasUnsecuredMember, setHasUnsecuredMember] = useState(false)
  const [replyToEventId, setReplyToEventId] = useState(null)

  const { chatCredentials, directRoomList } = useSelector(chatCredentialsSelectors)
  const { activeProfile } = useSelector(usersSelectors)

  const scrollRef = useRef(null)

  const directRoom = directRoomList?.find((r) => r?.roomId === roomId)
  const otherName = directRoom?.name ?? ''
  const otherAvatar = directRoom?.avatarUrl ?? ImagesPng.ProfileImage

  const dateFormatSeparator = (ts) => new Date(ts).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })

  const checkIfHasUnsecuredMember = async () => {
    const client = getMatrixClient()
    const crypto = client.getCrypto()
    const room = client.getRoom(roomId)

    await room?.loadMembersIfNeeded()
    const userIds = room?.getJoinedMembers().map((m) => m.userId)

    const devices = await crypto.getUserDeviceInfo(userIds, true)

    const status = userIds.map((userId) => ({
      userId,
      hasDevice: (devices.get(userId)?.size ?? 0) > 0
    }))

    setHasUnsecuredMember(!status.every((s) => s.hasDevice))
  }

  const handleChatSeparatorByDate = (date) => (
    <Flex justify="center" py={3}>
      <Text
        bg={COLORS.materialDesignGray800}
        color={COLORS.gray_op8}
        borderRadius="3px"
        px={4}
        py={1.5}
        fontSize="15px"
        lineHeight="1.25"
      >
        {date}
      </Text>
    </Flex>
  )

  useLayoutEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages])

  useEffect(() => {
    const client = getMatrixClient()
    const room = client?.getRoom(roomId)

    if (!room) {
      console.log('Room not found:', roomId)
      return undefined
    }

    const myId = client.getUserId()
    let scheduled = false
    let active = true

    const readChats = () => {
      scheduled = false
      if (!active) return

      const chats = room
        .getLiveTimeline()
        .getEvents()
        .filter((event) => (
          event.getType() === 'm.room.message'
          && !event.isRedacted()
          && (event.isEncrypted() || event.getSender() === myId)
        ))
        .map((event) => ({
          eventId: event.getId(),
          sender: event.getSender(),
          body: event.isDecryptionFailure()
            ? 'Unable to decrypt this message'
            : event.getContent()?.body ?? '',
          timestamp: event.getTs(),
          replyToId: event.getContent()?.['m.relates_to']?.['m.in_reply_to']?.event_id ?? null
        }))

      setMessages(chats)
    }

    const scheduleRead = () => {
      if (scheduled) {
        return
      }

      scheduled = true
      requestAnimationFrame(readChats)
    }

    const onTimeline = (event, eventRoom) => {
      if (eventRoom?.roomId === roomId) scheduleRead()
    }

    const onDecrypted = (event) => {
      if (event.getRoomId() === roomId) scheduleRead()
    }

    client.on('Room.timeline', onTimeline)
    client.on('Event.decrypted', onDecrypted)
    readChats()

    return () => {
      active = false
      client.removeListener('Room.timeline', onTimeline)
      client.removeListener('Event.decrypted', onDecrypted)
    }
  }, [roomId])

  const handleQuoteMsg = (msg) => {
    const quoted = messages.find((m) => m.eventId === msg?.replyToId)
    const quotedAuthor = members.find((m) => m.userId === quoted?.sender)

    if (
      quoted
      && quotedAuthor
    ) {
      const client = getMatrixClient()
      const room = client?.getRoom(roomId)
      const isMine = quotedAuthor.userId === room?.myUserId

      const author = isMine
        ? { ...quotedAuthor, avatarUrl: activeProfile?.profile_picture }
        : { ...quotedAuthor, avatarUrl: otherAvatar, name: otherName || quotedAuthor.name }

      return (
        <QuoteMessage
          quoted={quoted}
          quotedAuthor={author}
        />
      )
    }

    return ''
  }

  useEffect(() => {
    const client = getMatrixClient()
    const room = client?.getRoom(roomId)
    if (!room) {
      return undefined
    }

    const me = room?.myUserId
    let active = true

    const getMembers = async () => {
      try {
        await room?.loadMembersIfNeeded()
        if (!active) return

        const list = room?.getJoinedMembers().map((member) => {
          const mxcUrl = member.getMxcAvatarUrl()

          return {
            userId: member.userId,
            name: member.name,
            avatarUrl: mxcUrl
              ? client.mxcUrlToHttp(mxcUrl, 96, 96, 'crop', false, true, true)
              : null
          }
        })

        setMembers(list)
        setOtherUserId(list.find((m) => m.userId !== me)?.userId ?? null)
      } catch (error) {
        console.error('Unable to load room members:', error)
      }
    }

    const onMembership = (event, member) => {
      if (member?.roomId === roomId) {
        getMembers()
      }
    }

    client.on('RoomMember.membership', onMembership)
    getMembers()

    return () => {
      active = false
      client.removeListener('RoomMember.membership', onMembership)
    }
  }, [roomId])

  useEffect(() => {
    if (!memberCheck) {
      setMemberCheck(true)
      checkIfHasUnsecuredMember()
    }
  }, [])

  const handleGoToMarker = (userId) => {
    goToMarker(getProfileIdFromMatrixId(userId))
  }

  if (memberCheck) {
    if (hasUnsecuredMember) {
      return (
        <Flex
          height="100%"
          direction="column"
          align="center"
          justify="center"
          gap={3}
          px={6}
          textAlign="center"
        >
          <Avatar
            boxSize="90px"
            borderRadius="full"
            src={otherAvatar}
            cursor="pointer"
            onClick={() => handleGoToMarker(otherUserId)}
          />

          <Text
            fontSize="20px"
            fontWeight={600}
            color={COLORS.lightGray}
          >
            {otherName}
          </Text>

          <Box
            fontSize="15px"
            color={COLORS.gray_op8}
            maxW="260px"
          >
            Secured chat not activated yet.
            <Text marginTop="20px">You can message them once they open Connector and sign in.</Text>
          </Box>
        </Flex>
      )
    }

    return (
      <Flex
        direction="column"
        height="100%"
        minH="0"
      >
        <Flex
          ref={scrollRef}
          direction="column-reverse"
          flex="1"
          minH="0"
          overflowY="auto"
          sx={{
            overflowAnchor: 'none',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
              display: 'none'
            }
          }}
        >
          <Box
            mb="auto"
            height={messages.length ? 'auto' : '100%'}
          >
            {
              messages.length
                ? messages.map((msg, index) => {
                  const sentDT = new Date(msg?.timestamp).toLocaleString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  }).replace(' at ', ', ')

                  const date = dateFormatSeparator(msg?.timestamp)
                  let chatSeparatorByDate = ''
                  if (index === 0 || dateFormatSeparator(messages[index - 1].timestamp) !== date) {
                    chatSeparatorByDate = handleChatSeparatorByDate(date)
                  }

                  if (chatCredentials?.user_id === msg?.sender) {
                    return (
                      <>
                        {chatSeparatorByDate}

                        <Box
                          id={`msg-${msg.eventId}`}
                          role="group"
                          position="relative"
                        >
                          <MsgFunctionBar
                            eventId={msg.eventId}
                            setReplyToEventId={setReplyToEventId}
                            whosChat="me"
                          />

                          <Flex
                            key={msg.eventId}
                            align="flex-end"
                            bg={
                              replyToEventId === msg.eventId
                                ? COLORS.materialDesignGray800
                                : ''
                            }
                            justify="flex-end"
                            gap={4}
                            px={3}
                            py={2}
                          >
                            <Box
                              position="relative"
                              maxW="460px"
                              bg={COLORS.materialDesignGray800}
                              color={COLORS.lightGray}
                              borderRadius="3px"
                              borderBottomRightRadius="0"
                              py={2}
                              px={3}
                              _after={{
                                content: '""',
                                position: 'absolute',
                                right: '-14px',
                                bottom: 0,
                                borderLeft: '16px solid ' + COLORS.materialDesignGray800,
                                borderTop: '12px solid transparent'
                              }}
                            >
                              <Flex
                                direction="column"
                                justify="flex-end"
                              >
                                {msg.replyToId ? handleQuoteMsg(msg) : ''}

                                <Text
                                  fontSize="16px"
                                  lineHeight="1.25"
                                  wordBreak="break-word"
                                >
                                  {msg.body}
                                </Text>
                              </Flex>

                              <Flex
                                textAlign="right"
                                align="center"
                                justify="flex-end"
                                gap={1.5}
                                mt="5px"
                                color={COLORS.gray_op8}
                                fontSize="13px"
                              >
                                <Text>{sentDT}</Text>
                              </Flex>
                            </Box>

                            <Avatar
                              boxSize="55px"
                              borderRadius="lg"
                              src={activeProfile?.profile_picture ?? ImagesPng.ProfileImage}
                              flexShrink={0}
                              cursor="pointer"
                              onClick={() => handleGoToMarker(msg?.sender)}
                            />
                          </Flex>
                        </Box>
                      </>
                    )
                  }

                  return (
                    <>
                      {chatSeparatorByDate}

                      <Box
                        id={`msg-${msg.eventId}`}
                        role="group"
                        position="relative"
                      >
                        <MsgFunctionBar
                          eventId={msg.eventId}
                          setReplyToEventId={setReplyToEventId}
                          whosChat="them"
                        />

                        <Flex
                          key={msg.eventId}
                          align="flex-end"
                          justify="flex-start"
                          bg={
                            replyToEventId === msg.eventId
                              ? COLORS.materialDesignGray800
                              : ''
                          }
                          gap={4}
                          px={3}
                          py={2}
                        >
                          <Avatar
                            boxSize="55px"
                            borderRadius="lg"
                            src={otherAvatar}
                            flexShrink={0}
                            cursor="pointer"
                            onClick={() => handleGoToMarker(msg?.sender)}
                          />

                          <Box
                            position="relative"
                            maxW="460px"
                            bg={COLORS.materialDesignGray800}
                            color={COLORS.lightGray}
                            borderRadius="3px"
                            borderBottomLeftRadius="0"
                            py={2}
                            px={3}
                            _before={{
                              content: '""',
                              position: 'absolute',
                              left: '-14px',
                              bottom: 0,
                              borderRight: '16px solid ' + COLORS.materialDesignGray800,
                              borderTop: '12px solid transparent'
                            }}
                          >
                            <Flex
                              direction="column"
                              justify="flex-end"
                            >
                              {msg.replyToId ? handleQuoteMsg(msg) : ''}
                              <Text
                                fontSize="16px"
                                lineHeight="1.25"
                                wordBreak="break-word"
                              >
                                {msg.body}
                              </Text>
                            </Flex>

                            <Flex
                              align="center"
                              gap={1.5}
                              mt="5px"
                              color={COLORS.gray_op8}
                              fontSize="13px"
                            >
                              <Text>{sentDT}</Text>
                            </Flex>
                          </Box>
                        </Flex>
                      </Box>
                    </>
                  )
                })
                : (
                  <Flex
                    height="100%"
                    direction="column"
                    align="center"
                    justify="center"
                    gap={3}
                    px={6}
                    textAlign="center"
                  >
                    <Avatar
                      boxSize="90px"
                      borderRadius="full"
                      src={otherAvatar}
                      cursor="pointer"
                      onClick={() => handleGoToMarker(otherUserId)}
                    />

                    <Text
                      fontSize="20px"
                      fontWeight={600}
                      color={COLORS.lightGray}
                    >
                      {otherName}
                    </Text>

                    <Box
                      fontSize="15px"
                      color={COLORS.gray_op8}
                      maxW="260px"
                    >
                      No messages yet.
                      <Text>Say hi and start the conversation!</Text>
                    </Box>
                  </Flex>
                )
            }
          </Box>
        </Flex>

        <Box
          flexShrink={0}
          borderTop={'1px solid ' + COLORS.darkGray}
          backgroundColor="rgb(49, 49, 49)"
          color="white"
          minH="70px"
        >
          <InputBar
            roomId={roomId}
            hasUnsecuredMember={hasUnsecuredMember}
            replyToEventId={replyToEventId}
            setReplyToEventId={setReplyToEventId}
          />
        </Box>
      </Flex>
    )
  }

  return ''
}
